import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { metaAdsStatus, metaAdsStart, metaAdsDiscover, metaAdsSelect, metaAdsDisconnect } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FacebookLogo, CheckCircle, Warning, Hourglass, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

const BADGE = {
  connected: { cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25", Icon: CheckCircle },
  awaiting_account_selection: { cls: "bg-sky-500/15 text-sky-300 border-sky-500/25", Icon: Hourglass },
  no_accounts: { cls: "bg-red-500/10 text-red-300 border-red-500/25", Icon: Warning },
  missing_permissions: { cls: "bg-amber-500/10 text-amber-400 border-amber-500/25", Icon: Warning },
  token_expired: { cls: "bg-amber-500/10 text-amber-400 border-amber-500/25", Icon: Warning },
  api_error: { cls: "bg-amber-500/10 text-amber-400 border-amber-500/25", Icon: Warning },
  setup_required: { cls: "bg-white/5 text-[#9a9a9a] border-white/10", Icon: Warning },
  not_connected: { cls: "bg-white/5 text-[#9a9a9a] border-white/10", Icon: FacebookLogo },
};

export function MetaAdsConnect({ clientId }) {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const { data: s } = useQuery({
    queryKey: ["metaads-status", clientId],
    queryFn: () => metaAdsStatus(clientId),
    enabled: !!clientId,
    refetchInterval: (q) => (["api_error"].includes(q.state.data?.status) || q.state.data?.advancing ? 30000 : false),
  });

  const inv = () => { qc.invalidateQueries({ queryKey: ["metaads-status", clientId] }); qc.invalidateQueries({ queryKey: ["analytics-status", clientId] }); qc.invalidateQueries({ queryKey: ["ads", clientId] }); };
  const startM = useMutation({ mutationFn: () => metaAdsStart(clientId),
    onSuccess: (r) => { window.location.href = r.authorization_url; },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not start Facebook sign-in") });
  const discoverM = useMutation({ mutationFn: () => metaAdsDiscover(clientId),
    onSuccess: (r) => { inv(); if (r.connected) toast.success(r.message || "Connected and syncing"); else if (r.last_error) toast.error(r.last_error); else if (r.accounts?.length) setOpen(true); },
    onError: () => toast.error("Account discovery failed") });
  const selectM = useMutation({ mutationFn: (id) => metaAdsSelect(clientId, id),
    onSuccess: (r) => { inv(); if (r.connected) { setOpen(false); toast.success(r.message || "Connected and syncing"); } else toast.error(r.message || r.last_error || "Live test failed"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not select account") });
  const disconnectM = useMutation({ mutationFn: () => metaAdsDisconnect(clientId),
    onSuccess: () => { inv(); toast.success("Meta Ads disconnected"); } });

  useEffect(() => {
    const flag = params.get("metaads");
    if (!flag) return;
    if (flag === "authorized") {
      toast.success("Facebook authorization complete");
      inv();
      discoverM.mutate();
    } else if (flag === "error") {
      toast.error(`Facebook sign-in failed: ${params.get("reason") || "unknown error"}`);
    }
    params.delete("metaads"); params.delete("reason");
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!s) return null;
  const badge = BADGE[s.status] || BADGE.not_connected;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span data-testid="metaads-status-badge" className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${badge.cls}`} title={s.last_error || s.label}>
        <badge.Icon size={13} weight="fill" /> {s.label}
      </span>

      {(s.status === "not_connected" || s.status === "token_expired") && s.oauth_configured && (
        <Button data-testid="metaads-signin-btn" size="sm" onClick={() => startM.mutate()} disabled={startM.isPending}
          className="bg-[#1877F2] hover:bg-[#3b8bf5] text-white font-semibold">
          {startM.isPending ? <Spinner size={14} className="mr-1.5 animate-spin" /> : <FacebookLogo size={14} weight="bold" className="mr-1.5" />}
          Sign in with Facebook
        </Button>
      )}
      {(s.status === "awaiting_account_selection" || s.needs_discovery) && (
        <Button data-testid="metaads-choose-account-btn" size="sm" onClick={() => (s.accounts?.length ? setOpen(true) : discoverM.mutate())} disabled={discoverM.isPending}
          className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
          {discoverM.isPending ? <Spinner size={14} className="mr-1.5 animate-spin" /> : null}
          {s.accounts?.length ? "Choose ad account" : "Detect ad accounts"}
        </Button>
      )}
      {["no_accounts", "missing_permissions", "api_error"].includes(s.status) && (
        <Button data-testid="metaads-retry-btn" size="sm" variant="outline" onClick={() => discoverM.mutate()} disabled={discoverM.isPending}
          className="border-white/15 bg-transparent text-white">Re-check</Button>
      )}
      {s.authorized && (
        <Button data-testid="metaads-disconnect-btn" size="sm" variant="ghost" onClick={() => disconnectM.mutate()} className="text-[#9a9a9a] hover:text-white">Disconnect</Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-head">Select your Meta ad account</DialogTitle>
            <DialogDescription className="text-[#7a7a7a]">
              Signed in as {s.fb_name || "your Facebook profile"}. Vozelto detected these accessible ad accounts — pick the one to sync. A live read-only API test runs before the connection is confirmed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="metaads-account-list">
            {(s.accounts || []).map((a) => (
              <button key={a.id} data-testid={`metaads-account-${a.account_id}`}
                onClick={() => selectM.mutate(a.id)} disabled={selectM.isPending}
                className="w-full flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 hover:border-[#6B3DFF]/50 transition-colors text-left">
                <div>
                  <div className="text-sm font-semibold">{a.name}</div>
                  <div className="text-[11px] text-[#7a7a7a] font-mono">{a.account_id}{a.business ? ` · ${a.business}` : ""}{a.currency ? ` · ${a.currency}` : ""}</div>
                </div>
                {selectM.isPending ? <Spinner size={16} className="animate-spin text-[#6B3DFF]" /> : <span className="text-[11px] brand-text font-semibold">Connect →</span>}
              </button>
            ))}
            {!(s.accounts || []).length && <div className="text-sm text-[#7a7a7a] text-center py-4">No ad accounts detected yet.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
